import type {
  AdminUser,
  AdminUserListResponse,
  UsageLimit,
  UsageLimitPayload,
  UsageEvent,
  PlatformUsageStats,
} from "../types/admin";

import { apiBase, getAuthHeaders, handleJson } from "./http";

export const adminApi = {
  async listUsers(): Promise<AdminUser[]> {
    const response = await fetch(`${apiBase}/users`, {
      credentials: "include",
      headers: getAuthHeaders(),
    });
    const payload = await handleJson<AdminUserListResponse | AdminUser[]>(response);
    if (Array.isArray(payload)) return payload;
    return payload.users || [];
  },

  async createUser(payload: {
    email: string;
    display_name?: string | null;
    verified?: boolean;
  }): Promise<{ user_id: string; email: string }> {
    const response = await fetch(`${apiBase}/users`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });
    return handleJson(response);
  },

  async setAdminStatus(userId: string, isAdmin: boolean): Promise<void> {
    const response = await fetch(
      `${apiBase}/users/${encodeURIComponent(userId)}/admin`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ is_admin: isAdmin }),
      }
    );
    await handleJson(response);
  },

  async getUsageLimit(userId: string): Promise<UsageLimit | null> {
    const response = await fetch(
      `${apiBase}/users/${encodeURIComponent(userId)}/usage-limit`,
      {
        credentials: "include",
        headers: getAuthHeaders(),
      }
    );
    return handleJson<UsageLimit | null>(response);
  },

  async setUsageLimit(
    userId: string,
    payload: UsageLimitPayload
  ): Promise<UsageLimit> {
    const response = await fetch(
      `${apiBase}/users/${encodeURIComponent(userId)}/usage-limit`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      }
    );
    return handleJson<UsageLimit>(response);
  },

  async listUsageEvents(
    userId: string,
    params?: { start_at?: number; end_at?: number; limit?: number }
  ): Promise<UsageEvent[]> {
    const query = new URLSearchParams();
    if (params?.start_at != null) query.set("start_at", String(params.start_at));
    if (params?.end_at != null) query.set("end_at", String(params.end_at));
    if (params?.limit != null) query.set("limit", String(params.limit));
    const qs = query.toString();
    const response = await fetch(
      `${apiBase}/users/${encodeURIComponent(userId)}/usage-events${qs ? `?${qs}` : ""}`,
      {
        credentials: "include",
        headers: getAuthHeaders(),
      }
    );
    return handleJson<UsageEvent[]>(response);
  },

  async getPlatformStats(
    timeRange: string = "30d",
    startAt?: number,
    endAt?: number
  ): Promise<PlatformUsageStats> {
    const query = new URLSearchParams();
    query.set("time_range", timeRange);
    if (startAt != null) query.set("start_at", String(startAt));
    if (endAt != null) query.set("end_at", String(endAt));
    const response = await fetch(
      `${apiBase}/admin/usage/platform-stats?${query.toString()}`,
      {
        credentials: "include",
        headers: getAuthHeaders(),
      }
    );
    return handleJson<PlatformUsageStats>(response);
  },
};
